import { Skeleton, Stack, Text } from '@mantine/core'

import { useAdminRedeemable } from '@repo/common/queries/admin/redeem'
import { useLiquidityProviders } from '@repo/common/queries/admin/redeem/redeem_queries.ts'

import { TowerCard } from '@/components/ui/TowerCard'

import {
  BucketCard,
  type LiquidityPoolWithRedeemable,
  PoolCard,
} from './LiquidityOverview.tsx'

import classes from './LiquidityOverview.module.css'

export function RedeemableLiquidityPool({
  redeemableId,
}: {
  redeemableId: string
}) {
  const { data: redeemableData, isLoading: loadingRedeemable } =
    useAdminRedeemable(redeemableId)
  const { data: providersData, isLoading: loadingProviders } =
    useLiquidityProviders()

  const providers = providersData?.liquidity_providers.nodes ?? []

  const pool: LiquidityPoolWithRedeemable | null =
    redeemableData?.active_liquidity_pool
      ? {
          ...redeemableData.active_liquidity_pool,
          redeemable: { id: redeemableData.id, name: redeemableData.name },
        }
      : null

  const referencedBucketIds = new Set<string>()
  if (pool) {
    for (const tranche of pool.liquidity_pool_tranches.nodes) {
      for (const tb of tranche.liquidity_tranche_buckets.nodes) {
        referencedBucketIds.add(tb.liquidity_provider_bucket.id)
      }
    }
  }

  const filteredProviders = providers
    .map((p) => ({
      ...p,
      liquidity_provider_buckets: {
        nodes: p.liquidity_provider_buckets.nodes.filter((b) =>
          referencedBucketIds.has(b.id),
        ),
      },
    }))
    .filter((p) => p.liquidity_provider_buckets.nodes.length > 0)

  if (loadingRedeemable) {
    return (
      <Stack gap="lg">
        <Skeleton height={120} />
        <Skeleton height={60} />
      </Stack>
    )
  }

  if (!pool) {
    return <Text c="dimmed">No active liquidity pool for this redeemable</Text>
  }

  return (
    <Stack gap="lg" pb="50vh">
      <PoolCard pool={pool} />

      <TowerCard title="LP Buckets">
        {loadingProviders ? (
          <Stack gap="sm">
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Stack>
        ) : filteredProviders.length === 0 ? (
          <Text c="dimmed">No buckets linked to this pool</Text>
        ) : (
          <Stack gap="md">
            {filteredProviders.map((provider) => (
              <div key={provider.id} className={classes.pool_card}>
                <Text fw={600} mb="xs">
                  {provider.name}
                </Text>
                <Stack gap="sm">
                  {provider.liquidity_provider_buckets.nodes.map((bucket) => (
                    <BucketCard
                      key={bucket.id}
                      provider={provider}
                      bucket={bucket}
                    />
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
        )}
      </TowerCard>
    </Stack>
  )
}
